import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  bootstrapList,
  bootstrapCheck2All,
  bootstrapXCircle,
  bootstrapX,
} from '@ng-icons/bootstrap-icons';
import { IHeaderInfo } from '@core/interfaces/header.Interface';
import { CustomButtonComponent } from '@shared/components';
import { buttonConfig, customComponentType } from '@core/types/types';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicMaskPipe } from '@shared/pipes';
import { getThemeVariables } from 'src/app/theme/theme-variables';
import { RouteWatcherService } from '@core/services';
import { CreateClaimButtonComponent } from './components';
import { VoidClaimButtonComponent } from './components/void-claim-button/void-claim-button.component';
import { ClaimService } from '@core/services/http/claim.service';
import { ChangeDetectorRef } from '@angular/core';
import { SettlementService } from '@core/services/http/settlement.service';
import { CreateSettlementButtonComponent } from './components/create-settlement-button/create-settlement-button.component';
import { UndoClaimCreationProccessButtonComponent } from './components/undo-claim-creation-process-button/undo-claim-creation-process-button.component';
import { NgComponentOutlet } from '@angular/common';
import { environment } from '@env/environment';
import { sponsorSlugs } from '@core/enums/enums';

type navbarSectionItem = {
  key: 'docs';
  component: customComponentType;
};

@Component({
  selector: 'navbar',
  templateUrl: 'navbar.component.html',
  imports: [
    CustomButtonComponent,
    NgComponentOutlet,
    TranslateModule,
    DynamicMaskPipe,
    CreateClaimButtonComponent,
    VoidClaimButtonComponent,
    CreateSettlementButtonComponent,
    UndoClaimCreationProccessButtonComponent,
  ],
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PagesNavbarComponent {
  @HostBinding('style.display') display = '';
  @ViewChild('content') offCanvasContainer!: ElementRef;

  protected headerInfo?: IHeaderInfo = undefined;

  protected currentSection?: customComponentType = undefined;

  protected sections: navbarSectionItem[] = [
    {
      key: 'docs',
      component: {
        content: CustomButtonComponent,
        data: {
          config: {
            type: 'button',
            disabled: true,
            label: {
              key: environment.docs.version,
            },
            color: 'info',
            theme: 'badge',
          },
        },
      },
    },
  ];

  constructor(
    private readonly routeWatcher: RouteWatcherService,
    private readonly offcanvas: NgbOffcanvas,
    private readonly claimService: ClaimService,
    private readonly settlementService: SettlementService,
    private readonly cdr: ChangeDetectorRef
  ) {
    effect(() => {
      const initialClaimInfo = this.claimService.initialClaimInfo();
      const claim = this.claimService.claim();      

      if (initialClaimInfo) {
        this.headerInfo = {
          requestedCoverage: `app.pageId.${initialClaimInfo!.claimInfo.type}`,
          logo: initialClaimInfo!.sponsorSlug == sponsorSlugs.UNIVERSAL? initialClaimInfo!.sponsorSlug: 'koneksi-horizontal-full',
          transactionNumber: '20254-1234',
          receptionDate: claim?.requestedDate || initialClaimInfo.claimInfo.requestedDate || new Date(),
          userRepresentative: initialClaimInfo!.providerName,
          hasClaim: false,
        };

        this.cdr.detectChanges();
        return;
      } else if (claim) {
        this.headerInfo = {
          requestedCoverage: `app.pageId.${claim.serviceType}`,
          logo: claim.sponsorSlug == sponsorSlugs.UNIVERSAL? claim!.sponsorSlug: 'koneksi-horizontal-full',
          transactionNumber: '20254-1234',
          receptionDate: claim.requestedDate,
          userRepresentative: claim.providerSlug,
          hasClaim: true,
        };

        this.cdr.detectChanges();
      }

      const initialSettlementInfo =
        this.settlementService.initialSettlementInfo();
      const settlement = this.settlementService.settlement();

      if (settlement) {
        this.headerInfo = {
          requestedCoverage: `app.pageId.settlement.settlement`,
          logo: settlement!.sponsorSlug == sponsorSlugs.UNIVERSAL? settlement!.sponsorSlug: 'koneksi-horizontal-full',
          transactionNumber: '20254-1234',
          receptionDate: settlement.requestDate!,
          userRepresentative: settlement.providerSlug,
          hasClaim: true,
        };

        this.cdr.detectChanges();
      } else if (initialSettlementInfo) {
        this.headerInfo = {
          requestedCoverage: `app.pageId.settlement.settlement`,
          logo: initialSettlementInfo!.sponsorSlug == sponsorSlugs.UNIVERSAL? initialSettlementInfo!.sponsorSlug: 'koneksi-horizontal-full',
          transactionNumber: '20254-1234',
          receptionDate: new Date(),
          userRepresentative: initialSettlementInfo!.providerName,
          hasClaim: false,
        };

        this.cdr.detectChanges();
      }

      this.currentSection = this.sections.find(
        (section) =>
          section.key ==
          routeWatcher.getCurrentSection([
            'docs',
            'medical-encounter',
            'medical-procedure',
            'laboratory',
            'settlement',
            'consult-claim',
          ])
      )?.component;
    });
  }

  protected icons = {
    doubleCheck: bootstrapCheck2All,
    xCircle: bootstrapXCircle,
    x: bootstrapX,
    list: bootstrapList,
  };

  protected configs = computed(() => ({
    btn: {
      openOffCanvas: {
        type: 'icon',
        icon: this.icons.list,
        color: 'primary',
        size: 'md',
        theme: 'ghost',
        hidden: window.innerWidth > getThemeVariables().breakpoints.lg,
        action: () => this.openOffCanvas(this.offCanvasContainer),
      } as buttonConfig,
      closeOffCanvas: {
        type: 'icon',
        icon: this.icons.x,
        color: 'info',
        size: 'md',
        theme: 'ghost',
        action: () => this.closeOffCanvas(this.offCanvasContainer),
      } as buttonConfig,
    },
  }));

  openOffCanvas(content: ElementRef) {
    this.offcanvas.open(content);
  }

  closeOffCanvas(content: ElementRef) {
    this.offcanvas.dismiss(content);
  }
}
